import { useState } from 'react';
import { BRAND, FONTS } from '../styles/tokens';
import { Reveal, SectionLabel } from './ui/Reveal';
import { TRAININGS } from '../data/trainings';
import { useIsMobile } from '../hooks/useIsMobile';
import { TRAINING_INTEREST } from '../data/content';
import type { LeadPrefill } from '../types';
import { pagePath } from '../data/pages';

interface TrainingProps { onLead: (p: LeadPrefill) => void }

export function Training({ onLead }: TrainingProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const m = useIsMobile();
  return (
    <section id="training" style={{ background: BRAND.navy, padding: m ? '64px 16px' : '100px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal>
          <div style={{ textAlign: 'center', marginBottom: m ? 36 : 56 }}>
            <SectionLabel center>Training Topics</SectionLabel>
            <h2 style={{ fontFamily: FONTS.heading, fontSize: m ? 28 : 42, fontWeight: 700, color: BRAND.white, lineHeight: 1.15, marginBottom: 14 }}>
              State-recognized training{!m && <br />} built for real-world response.
            </h2>
            <p style={{ fontFamily: FONTS.body, fontSize: m ? 14 : 15, color: 'rgba(255,255,255,0.45)', maxWidth: 520, margin: '0 auto' }}>
              Tap any topic to see what's covered.
            </p>
          </div>
        </Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(auto-fit, minmax(340px, 1fr))', gap: m ? 12 : 18 }}>
          {TRAININGS.map((t, i) => {
            const isOpen = expanded === t.id;
            return (
              <Reveal key={t.id} delay={m ? 0 : i * 0.06}>
                <div onClick={() => setExpanded(isOpen ? null : t.id)}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(isOpen ? null : t.id); } }}
                  role="button" tabIndex={0} aria-expanded={isOpen}
                  style={{ background: isOpen ? 'rgba(124,58,165,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${isOpen ? `${BRAND.warm}50` : 'rgba(255,255,255,0.06)'}`, borderRadius: 12, padding: m ? 20 : 28, cursor: 'pointer', transition: 'all 0.35s ease' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', gap: m ? 12 : 14, alignItems: 'center' }}>
                      <span style={{ fontSize: m ? 22 : 26 }}>{t.icon}</span>
                      <div>
                        <h3 style={{ fontFamily: FONTS.heading, fontSize: m ? 17 : 20, fontWeight: 700, color: BRAND.white, marginBottom: 4 }}>{t.title}</h3>
                        <div style={{ fontFamily: FONTS.body, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: BRAND.warm }}>{t.audience}</div>
                      </div>
                    </div>
                    <span style={{ color: BRAND.warm, fontSize: 20, transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.3s', flexShrink: 0, marginTop: 4 }}>+</span>
                  </div>
                  <p style={{ fontFamily: FONTS.body, fontSize: m ? 13 : 14, lineHeight: 1.7, color: 'rgba(255,255,255,0.5)', marginTop: 12 }}>{t.short}</p>
                  <div style={{ maxHeight: isOpen ? 500 : 0, overflow: 'hidden', transition: 'max-height 0.4s ease, opacity 0.3s ease', opacity: isOpen ? 1 : 0 }}>
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 16, paddingTop: 16 }}>
                      <div style={{ fontFamily: FONTS.body, fontSize: 12, fontWeight: 600, color: BRAND.warm, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>What's covered</div>
                      {t.details.map((d, j) => (
                        <div key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8 }}>
                          <div style={{ width: 5, height: 5, borderRadius: '50%', background: BRAND.warm, flexShrink: 0, marginTop: 7 }} />
                          <span style={{ fontFamily: FONTS.body, fontSize: 13, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>{d}</span>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: FONTS.body, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>⏱ {t.duration}</span>
                        <span style={{ fontFamily: FONTS.body, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>📍 {t.format}</span>
                      </div>
                      <a href={pagePath('program', t.slug)} onClick={e => e.stopPropagation()} style={{ display: 'inline-block', marginTop: 14, marginRight: 16, fontFamily: FONTS.body, fontSize: 13, fontWeight: 600, color: BRAND.teal, textDecoration: 'none' }}>Learn more ↗</a>
                      <button onClick={(e) => { e.stopPropagation(); onLead({ interest: TRAINING_INTEREST, message: `Training of interest: ${t.title}` }); }}
                        style={{ marginTop: 16, background: BRAND.warm, color: BRAND.navy, border: 'none', padding: m ? '14px 24px' : '10px 24px', borderRadius: m ? 10 : 6, fontSize: 13, fontWeight: 700, fontFamily: FONTS.body, cursor: 'pointer', width: m ? '100%' : 'auto' }}>
                        Request This Training
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
